import React, { useState, useEffect, useCallback, useRef } from 'react';
import API_URL from '../config';
import { useToast, parseApiError } from '../context/ToastContext';

function SimulationLab({ token }) {
  const toast = useToast();
  const [simulations, setSimulations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [cases, setCases] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', agent_id: '', cases: [{ input: '', expected_output: '' }] });
  const pollRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchSimulations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/simulations`, { headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to load simulations'));
      setSimulations((await res.json()).simulations || []);
    } catch (e) { toast(e.message, 'error'); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/agents`, { headers });
      if (!res.ok) return;
      setAgents((await res.json()).agents || []);
    } catch {}
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchSimulations(); fetchAgents(); }, [fetchSimulations, fetchAgents]);

  // Stop any running poll
  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => () => stopPolling(), []);

  const loadSimulation = useCallback(async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/protected/simulations/${id}`, { headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to load simulation'));
      const data = await res.json();
      setSelected(data.simulation);
      setCases(data.cases || []);
      return data.simulation;
    } catch (e) { toast(e.message, 'error'); return null; }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSimulation = async (id) => {
    stopPolling();
    const sim = await loadSimulation(id);
    // If already running (e.g. page refresh mid-run), resume polling
    if (sim && sim.status === 'running') startPolling(id);
  };

  const startPolling = (id) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const sim = await loadSimulation(id);
      if (sim && (sim.status === 'completed' || sim.status === 'failed')) {
        stopPolling();
        fetchSimulations();
        if (sim.status === 'completed') toast('Simulation complete!', 'success');
      }
    }, 2000);
  };

  const createSimulation = async (andRun = false) => {
    if (!form.name.trim()) { toast('Simulation name is required', 'warning'); return; }
    const validCases = form.cases.filter(c => c.input.trim());
    if (validCases.length === 0) { toast('Add at least one test case', 'warning'); return; }
    try {
      const body = { name: form.name, description: form.description, cases: validCases };
      if (form.agent_id) body.agent_id = parseInt(form.agent_id);
      const res = await fetch(`${API_URL}/api/protected/simulations`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to create simulation'));
      const data = await res.json();
      setShowCreate(false);
      setForm({ name: '', description: '', agent_id: '', cases: [{ input: '', expected_output: '' }] });
      fetchSimulations();
      await openSimulation(data.id);
      if (andRun && form.agent_id) runSimulation(data.id);
    } catch (e) { toast(e.message, 'error'); }
  };

  const runSimulation = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/protected/simulations/${id}/run`, { method: 'POST', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to start simulation'));
      // Optimistically update status and reset cases
      setSelected(prev => prev ? { ...prev, status: 'running', passed_count: 0 } : prev);
      setCases(prev => prev.map(c => ({ ...c, status: 'pending', actual_output: null, score: null })));
      startPolling(id);
      toast('Simulation running — results will appear automatically', 'success');
    } catch (e) { toast(e.message, 'error'); }
  };

  const deleteSimulation = async (id) => {
    if (!window.confirm('Delete this simulation?')) return;
    try {
      const res = await fetch(`${API_URL}/api/protected/simulations/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to delete simulation'));
      stopPolling();
      setSelected(null);
      fetchSimulations();
    } catch (e) { toast(e.message, 'error'); }
  };

  const addCase = () => setForm({ ...form, cases: [...form.cases, { input: '', expected_output: '' }] });
  const removeCase = (idx) => setForm({ ...form, cases: form.cases.filter((_, i) => i !== idx) });
  const updateFormCase = (idx, field, value) => {
    const newCases = [...form.cases];
    newCases[idx][field] = value;
    setForm({ ...form, cases: newCases });
  };

  const statusBadge = (status) => {
    const map = {
      pending:   'badge-neutral',
      running:   'badge-info',
      completed: 'badge-success',
      failed:    'badge-error',
      passed:    'badge-success',
      error:     'badge-error',
    };
    return <span className={`badge ${map[status] || 'badge-neutral'}`}>{status}</span>;
  };

  // ── Detail view ─────────────────────────────────────────────────────────────
  if (selected) {
    const passRate = selected.case_count > 0
      ? ((selected.passed_count / selected.case_count) * 100).toFixed(1)
      : '0';
    const isRunning = selected.status === 'running';
    const completedCases = cases.filter(c => c.status !== 'pending').length;
    const progress = cases.length > 0 ? (completedCases / cases.length) * 100 : 0;

    return (
      <>
        <div className="page-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="btn-ghost" onClick={() => { stopPolling(); setSelected(null); }}>&larr; Back</button>
              <div className="page-title">{selected.name}</div>
              {statusBadge(selected.status)}
            </div>
            <div className="page-subtitle">{selected.description || 'No description'}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isRunning && (
              <button className="btn-primary" onClick={() => runSimulation(selected.id)}>
                ▶ Run Simulation
              </button>
            )}
            {isRunning && (
              <button className="btn-secondary" disabled style={{ opacity: 0.7 }}>
                ⏳ Running...
              </button>
            )}
            <button className="btn-danger btn-sm" onClick={() => deleteSimulation(selected.id)}>Delete</button>
          </div>
        </div>
        <div className="page-body">
          {/* Stat cards */}
          <div className="metrics-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="metric-card blue">
              <div className="metric-label">Total Cases</div>
              <div className="metric-value blue">{selected.case_count}</div>
            </div>
            <div className="metric-card green">
              <div className="metric-label">Passed</div>
              <div className="metric-value green">{selected.passed_count}</div>
            </div>
            <div className="metric-card yellow">
              <div className="metric-label">Pass Rate</div>
              <div className="metric-value yellow">{passRate}%</div>
            </div>
            <div className="metric-card purple">
              <div className="metric-label">Pending</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--warning)', marginTop: 4 }}>
                {cases.filter(c => c.status === 'pending').length}
              </div>
            </div>
          </div>

          {/* Running progress bar */}
          {isRunning && (
            <div style={{
              background: 'var(--bg-card)', border: '1px solid var(--accent)',
              borderRadius: 8, padding: '16px 20px', marginBottom: 16,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  Running — {completedCases}/{cases.length} cases complete
                </span>
                <span className="text-sm text-muted">Auto-refreshing every 2s</span>
              </div>
              <div style={{ background: 'var(--bg-primary)', borderRadius: 4, height: 8 }}>
                <div style={{
                  background: 'var(--accent)', width: `${progress}%`, height: 8,
                  borderRadius: 4, transition: 'width 0.5s ease',
                }} />
              </div>
            </div>
          )}

          {/* No agent configured warning */}
          {!isRunning && selected.status === 'pending' && !selected.agent_id && (
            <div style={{
              background: 'var(--bg-card)', border: '1px solid var(--warning)',
              borderRadius: 8, padding: '16px 20px', marginBottom: 16,
            }}>
              <div style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: 4 }}>No agent linked</div>
              <div className="text-sm text-muted">
                This simulation has no agent assigned. Running will fail. Go back, delete this simulation,
                and create a new one with an agent selected — or link an agent to this simulation.
              </div>
            </div>
          )}

          {/* Test cases table */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Test Cases</div>
              {selected.status === 'completed' && (
                <button className="btn-ghost btn-sm" onClick={() => runSimulation(selected.id)}>↺ Re-run</button>
              )}
            </div>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Input</th>
                  <th>Expected</th>
                  <th>Actual Output</th>
                  <th>Score</th>
                  <th>Status</th>
                  {cases.some(c => c.duration_ms != null) && <th>ms</th>}
                </tr>
              </thead>
              <tbody>
                {cases.map((c, i) => (
                  <tr key={c.id}>
                    <td>{i + 1}</td>
                    <td style={{ maxWidth: 180 }}>
                      <div className="truncate" title={c.input}>{c.input}</div>
                    </td>
                    <td style={{ maxWidth: 140 }}>
                      <div className="truncate" title={c.expected_output || ''}>{c.expected_output || '—'}</div>
                    </td>
                    <td style={{ maxWidth: 200 }}>
                      {c.status === 'pending' && isRunning
                        ? <span className="text-muted" style={{ fontSize: 12 }}>waiting...</span>
                        : c.error_message
                          ? <span style={{ color: 'var(--error)', fontSize: 12 }} title={c.error_message}>Error: {c.error_message.slice(0, 50)}</span>
                          : <div className="truncate" title={c.actual_output || ''} style={{ color: c.actual_output ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                              {c.actual_output || '—'}
                            </div>
                      }
                    </td>
                    <td>
                      {c.score != null
                        ? <span style={{ color: c.score >= 1.0 ? 'var(--success)' : 'var(--error)', fontWeight: 600 }}>
                            {c.score.toFixed(2)}
                          </span>
                        : '—'}
                    </td>
                    <td>{statusBadge(c.status)}</td>
                    {cases.some(c2 => c2.duration_ms != null) && (
                      <td className="text-muted text-xs">{c.duration_ms != null ? `${c.duration_ms}ms` : ''}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────
  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Simulation Lab</div>
          <div className="page-subtitle">Test agents automatically against datasets and detect regressions</div>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New Simulation</button>
      </div>
      <div className="page-body">
        {showCreate && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 16 }}>Create Simulation</div>
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Customer Support v2"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="What are you testing?"
                />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>Agent *</label>
              {agents.length === 0
                ? <div className="text-sm text-muted">No agents registered — <a href="#agents" style={{ color: 'var(--accent)' }}>go to Agents tab to register one first</a></div>
                : (
                  <select
                    value={form.agent_id}
                    onChange={e => setForm({ ...form, agent_id: e.target.value })}
                    style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', width: '100%' }}
                  >
                    <option value="">— Select agent —</option>
                    {agents.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} {a.has_api_key ? '✓ configured' : '⚠ no API key'}
                      </option>
                    ))}
                  </select>
                )
              }
              {form.agent_id && !agents.find(a => String(a.id) === form.agent_id)?.has_api_key && (
                <div className="text-sm" style={{ color: 'var(--warning)', marginTop: 4 }}>
                  ⚠ This agent has no API key configured. Go to Agents → Configure before running.
                </div>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 8 }}>Test Cases</label>
              {form.cases.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                  <input
                    placeholder="Input prompt to send to agent..."
                    value={c.input}
                    onChange={e => updateFormCase(i, 'input', e.target.value)}
                    style={{ flex: 2 }}
                  />
                  <input
                    placeholder="Expected output (for auto-scoring)"
                    value={c.expected_output}
                    onChange={e => updateFormCase(i, 'expected_output', e.target.value)}
                    style={{ flex: 2 }}
                  />
                  {form.cases.length > 1 && (
                    <button className="btn-ghost btn-sm" onClick={() => removeCase(i)} style={{ marginTop: 2 }}>✕</button>
                  )}
                </div>
              ))}
              <button className="btn-ghost btn-sm" onClick={addCase}>+ Add case</button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={() => createSimulation(true)}
                disabled={!form.agent_id || !agents.find(a => String(a.id) === form.agent_id)?.has_api_key}>
                Create & Run
              </button>
              <button className="btn-secondary" onClick={() => createSimulation(false)}>Create Only</button>
              <button className="btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
            {form.agent_id && !agents.find(a => String(a.id) === form.agent_id)?.has_api_key && (
              <div className="text-xs text-muted" style={{ marginTop: 8 }}>
                "Create & Run" is disabled until the agent is configured. Use "Create Only" and configure the agent first.
              </div>
            )}
          </div>
        )}

        {simulations.length === 0 && !showCreate ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">⊘</div>
              <h3>No simulations yet</h3>
              <p>Create test suites and Agent Oversight will automatically run them against your agent's AI model — no manual copy-paste required.</p>
              <button className="btn-primary" onClick={() => setShowCreate(true)}>Create First Simulation</button>
            </div>
          </div>
        ) : (
          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Agent</th>
                  <th>Cases</th>
                  <th>Pass Rate</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {simulations.map(s => (
                  <tr key={s.id} className="clickable-row" onClick={() => openSimulation(s.id)}>
                    <td>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{s.name}</div>
                      {s.description && <div className="text-xs text-muted">{s.description}</div>}
                    </td>
                    <td>{s.agent_name || <span className="text-muted">—</span>}</td>
                    <td>{s.case_count}</td>
                    <td>
                      {s.case_count > 0
                        ? <span style={{ color: s.passed_count === s.case_count && s.case_count > 0 ? 'var(--success)' : 'var(--text-primary)' }}>
                            {((s.passed_count / s.case_count) * 100).toFixed(0)}%
                          </span>
                        : '—'}
                    </td>
                    <td>{statusBadge(s.status)}</td>
                    <td className="text-sm">{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default SimulationLab;
